import { useMutation } from "@apollo/client";
import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";
import { DOCUMENTS } from "routes/Customer/Documents";

import FileIcon from "@mui/icons-material/InsertDriveFile";
import PreviewIcon from "@mui/icons-material/Preview";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { Dropzone } from "components/Dropzone";
import { gql } from "generated-graphql";
import { CreateDocumentInput } from "generated-graphql/graphql";
import { useUploadFiles } from "hooks/useDocumentUpload";
import compact from "lodash/compact";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { DocumentAIAnalyzingDialog } from "./DocumentAIAnalyzingDialog";

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  }
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
};

const QUEUE_DOCUMENT_AI_ENRICHMENT_JOB_MUTATION = gql(`
  mutation QueueDocumentAIEnrichmentJob($tenantId: ID!, $documents: [CreateDocumentInput!]!) {
    queueDocumentAIEnrichmentJob(input: { tenantId: $tenantId, documents: $documents }) {
      id
      status
      documents {
        id
        title
        extractionStatus
      }
    }
  }
`);
export type AddEditDocumentWithAIEnrichmentDialogProps = {
  open: boolean;
  title?: string;
  onClose: (
    event: React.MouseEvent<HTMLElement>,
    reason:
      | "backdropClick"
      | "escapeKeyDown"
      | "cancelButtonClick"
      | "closeButtonClick"
  ) => void;

  /** Maximum number of files to upload. Defaults to 10 */
  maxFiles?: number;
  disabled?: boolean;
  tenantId: string;
};

export function AddEditDocumentWithAIEnrichmentDialog(
  props: AddEditDocumentWithAIEnrichmentDialogProps
) {
  const {
    tenantId,
    open,
    title = "Add Document(s)",
    onClose,
    maxFiles = 10,
    disabled,
  } = props;

  const alerts = useAlerts();
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [analyzingOpen, setAnalyzingOpen] = useState(false);
  const [queuedJobId, setQueuedJobId] = useState<string | null>(null);
  const canAddMore = files.length < maxFiles;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;
      setFiles((prev) => {
        const remaining = Math.max(0, maxFiles - prev.length);
        const next = [...prev, ...acceptedFiles.slice(0, remaining)];
        return next;
      });
    },
    [maxFiles]
  );

  const handleRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handlePreview = useCallback((file: File) => {
    setPreviewFile(file);
  }, []);

  const onFillManually = useCallback((files: File[]) => {
    console.log("onFillManually", files);
    setFiles(files);
  }, []);

  const uploadFiles = useUploadFiles();

  const [
    queueDocumentAIEnrichmentJob,
    { loading: queueDocumentAIEnrichmentJobLoading },
  ] = useMutation(QUEUE_DOCUMENT_AI_ENRICHMENT_JOB_MUTATION, {
    refetchQueries: [FACILITY_DOCUMENTS, DOCUMENTS],
  });

  const onUploadAndAutofill = useCallback(
    async (files: File[]) => {
      setAnalyzingOpen(true);
      setQueuedJobId(null);
      try {
        const keys = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );
        const storageLinks = compact(keys);
        if (storageLinks.length !== keys.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }

        const documentData: CreateDocumentInput[] = storageLinks.map(
          (storageLink, i) => ({
            storageLink,
            title: files[i].name,
            fileExtension: files[i].name.split(".").pop()?.toLowerCase() ?? "",
          })
        );

        const res = await queueDocumentAIEnrichmentJob({
          variables: { documents: documentData, tenantId },
        });
        const newJobId = res?.data?.queueDocumentAIEnrichmentJob?.id;

        if (newJobId) {
          setQueuedJobId(newJobId);
        }
      } catch (error) {
        alerts.error(
          "Something went wrong while uploading and beginning the document analysis process.",
          error
        );
      }
    },
    [uploadFiles, tenantId, queueDocumentAIEnrichmentJob, alerts]
  );

  const previewUrl = useMemo(() => {
    if (!previewFile) return "";
    return URL.createObjectURL(previewFile);
  }, [previewFile]);

  if (analyzingOpen) {
    return (
      <DocumentAIAnalyzingDialog
        open={analyzingOpen}
        onClose={(e, reason) => {
          onClose(e, reason);
          setAnalyzingOpen(false);
        }}
        jobId={queuedJobId}
        fileNames={files.map((f) => f.name)}
      />
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        title={title}
        cancelButtonProps={{
          onClick: () => onFillManually(files),
          disabled: disabled || files.length === 0,
          text: "Fill Manually Instead",
        }}
        primaryActionButtonProps={{
          onClick: () => onUploadAndAutofill(files),
          disabled: disabled || files.length === 0,
          saveText: "Upload & Autofill",
          loading: analyzingOpen || queueDocumentAIEnrichmentJobLoading,
        }}
      >
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Drag and drop or select up to 10 files. We'll try to detect the type
            of each document and fill in the details for you.
          </Typography>

          {files.length > 0 && (
            <List dense>
              {files.map((file, idx) => (
                <Stack
                  key={`${file.name}-${idx}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  borderRadius="4px"
                  sx={{ "&:not(:last-child)": { mb: theme.spacing(3) } }}
                >
                  <ListItem
                    sx={{ bgcolor: "#517BC61F", borderRadius: "4px" }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="preview-document"
                        onClick={() => handlePreview(file)}
                        color="primary"
                      >
                        <PreviewIcon />
                      </IconButton>
                    }
                  >
                    <Stack
                      sx={{
                        minWidth: theme.spacing(5),
                        justifyContent: "center",
                      }}
                    >
                      <FileIcon color="action" />
                    </Stack>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                    />
                  </ListItem>
                  <IconButton
                    size="medium"
                    aria-label="remove-document"
                    onClick={() => handleRemove(idx)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Stack>
              ))}
            </List>
          )}

          <Dropzone
            label={
              canAddMore
                ? "Drag and drop your document or click to select file(s)"
                : "File limit reached"
            }
            dragActiveText="Drop files here"
            onDrop={onDrop}
            accept={".pdf"}
            multiple
            disabled={disabled || !canAddMore}
            sx={{ height: theme.spacing(8) }}
          />
          {files.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {files.length} {pluralize("file", files.length)} selected
            </Typography>
          )}
        </Stack>
      </Dialog>

      {previewFile && (
        <Dialog
          open={!!previewFile}
          onClose={() => {
            setPreviewFile(null);
          }}
          fullWidth
          maxWidth="lg"
          title={previewFile.name}
          noPrimaryAction
        >
          <Box sx={{ height: theme.spacing(100) }}>
            {previewUrl ? (
              <iframe
                title="file-preview"
                src={previewUrl}
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            ) : (
              <Typography variant="body2">No preview available.</Typography>
            )}
          </Box>
        </Dialog>
      )}
    </>
  );
}
